/**
 * Organization-keyed localStorage helpers for offline caches.
 * Keys: `${baseKey}::${organizationId}` with legacy flat-key fallback on read.
 */

import { loadStoredMembershipOrgId } from "./membershipHelpers";

export const ORG_CACHE_SEP = "::";
export const CACHE_ORG_KEY = "CP_CACHE_ORG_ACTIVE";

export function buildOrgScopedCacheKey(baseKey, organizationId) {
  if (!baseKey || !organizationId) {
    return baseKey;
  }
  return `${baseKey}${ORG_CACHE_SEP}${organizationId}`;
}

export function getActiveCacheOrganizationId() {
  try {
    return localStorage.getItem(CACHE_ORG_KEY);
  } catch {
    return null;
  }
}

export function stampActiveCacheOrganization(organizationId) {
  if (!organizationId) {
    return;
  }
  localStorage.setItem(CACHE_ORG_KEY, organizationId);
}

export function resolveCacheOrganizationId(actor) {
  return (
    actor?.organization_id ||
    loadStoredMembershipOrgId() ||
    getActiveCacheOrganizationId() ||
    null
  );
}

function readJsonKey(key) {
  if (!key) {
    return null;
  }
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Read org-scoped JSON. When organizationId is known, only the org bucket is read
 * (no legacy flat-key fallback — prevents cross-org bleed). Without organizationId,
 * reads the legacy flat key for backward compatibility.
 */
export function readOrgScopedJson(baseKey, organizationId) {
  if (!baseKey) {
    return null;
  }

  if (organizationId) {
    const orgKey = buildOrgScopedCacheKey(baseKey, organizationId);
    const orgValue = readJsonKey(orgKey);
    if (orgValue != null) {
      return orgValue;
    }
    return null;
  }

  return readJsonKey(baseKey);
}

export function writeOrgScopedJson(baseKey, organizationId, value) {
  if (!baseKey) {
    return;
  }

  if (organizationId) {
    const orgKey = buildOrgScopedCacheKey(baseKey, organizationId);
    localStorage.setItem(orgKey, JSON.stringify(value));
    stampActiveCacheOrganization(organizationId);
    return;
  }

  localStorage.setItem(baseKey, JSON.stringify(value));
}

/**
 * Remove a base key and all `${baseKey}::${orgId}` variants.
 */
export function removeOrgScopedKeysForBase(baseKey) {
  if (!baseKey) {
    return;
  }

  const toRemove = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key === baseKey || key?.startsWith(`${baseKey}${ORG_CACHE_SEP}`)) {
      toRemove.push(key);
    }
  }
  toRemove.forEach((key) => localStorage.removeItem(key));
}
